<template>
    <modal size="large" @close="$emit('close')">
        <template #header>
            <div>
                <span v-if="type" class="font-weight-bold" style="text-transform: capitalize;">
                    {{ type.split('_').join(' ') }}
                </span>
            </div>
        </template>

        <template #body>
			<div v-if="type === 'confirm'">
				<span>
					{{ label }}
				</span>

				<div v-if="isDropshipper" class="form-group mt-4">
					<label class="text-bold">Proof of Delivery (PoD)<span class="text-danger">*</span></label>
					<div v-if="podImage">
						<div class="mb-2 w-100">
							<img :src="podImage.url" class="border-grey border-radius-8" />
						</div>
						<div class="d-flex">
							<div class="text-break">
								<div class="text-bold">
									<span class="mr-2">{{ podImage.name }}</span>
									<i class="bi bi-trash text-danger cursor-pointer" @click="podImage = null"></i>
								</div>
								<div>
									<small><i>Original Name : {{ podImage.original_name }}</i></small>
								</div>
								<small>{{ podImage.size.toFixed(2) }} MB</small>
							</div>
						</div>
					</div>
					<drag-and-drop
						:is-error="errorMessages.podImage.length > 0"
						:type="'image'"
						@set-url="setPodUrl"
						v-if="!podImage"
					/>
					<small class="text-danger" v-if="errorMessages.podImage">{{ errorMessages.podImage }}</small>
				</div>

				<div v-else-if="!data.shipping.receipt_number" class="form-group mt-4">
					<label class="text-bold">Proof of Shipping (PoS)<span class="text-danger">*</span></label>
					<div v-if="posImage">
						<div class="mb-2 w-100">
							<img :src="posImage.url" class="border-grey border-radius-8" />
						</div>
						<div class="d-flex">
							<div class="text-break">
								<div class="text-bold">
									<span class="mr-2">{{ posImage.name }}</span>
									<i class="bi bi-trash text-danger cursor-pointer" @click="posImage = null"></i>
								</div>
								<div>
									<small><i>Original Name : {{ posImage.original_name }}</i></small>
								</div>
								<small>{{ posImage.size.toFixed(2) }} MB</small>
							</div>
						</div>
					</div>
					<drag-and-drop
						:is-error="errorMessages.posImage.length > 0"
						:type="'image'"
						@set-url="setPosUrl"
						v-if="!posImage"
					/>
					<small class="text-danger" v-if="errorMessages.posImage">{{ errorMessages.posImage }}</small>
				</div>
			</div>
			<div v-else-if="type === 'input_receipt_number'">
				<div>
					<div>
						<span>
							Set Order <b>#{{ data.order_id }}</b> Receipt Number
						</span>
					</div>
					<div class="form-group mt-4">
						<label>
							<b>Receipt Number</b>
						</label>
						<input
							v-model="receiptNumber"
							class="form-control"
							placeholder="Enter Receipt Number"
						/>
					</div>

					<div v-if="!data.shipping.receipt_number" class="form-group">
						<label class="text-bold">Proof of Shipping (PoS)<span class="text-danger">*</span></label>
						<div v-if="posImage">
							<div class="mb-2 w-100">
								<img :src="posImage.url" class="border-grey border-radius-8" />
							</div>
							<div class="d-flex">
								<div class="text-break">
									<div class="text-bold">
										<span class="mr-2">{{ posImage.name }}</span>
										<i class="bi bi-trash text-danger cursor-pointer" @click="posImage = null"></i>
									</div>
									<div>
										<small><i>Original Name : {{ posImage.original_name }}</i></small>
									</div>
									<small>{{ posImage.size.toFixed(2) }} MB</small>
								</div>
							</div>
						</div>
						<drag-and-drop
							:is-error="errorMessages.posImage.length > 0"
							:type="'image'"
							@set-url="setPosUrl"
							v-if="!posImage"
						/>
						<small class="text-danger" v-if="errorMessages.posImage">{{ errorMessages.posImage }}</small>
					</div>

					<div v-if="data.shipping.receipt_number" class="mt-4">
						<button class="btn btn-light flex-center mb-2 ml-auto mr-auto" @click="swicth">
							<i class="material-icons icons">
								import_export
							</i>
							Switch
						</button>
					</div>

					<div v-if="data.shipping.receipt_number" class="form-group mt-4">
						<label>
							<b>Cancelled Receipt Number</b>
						</label>
						<input
							v-model="cancelledReceiptNumber"
							class="form-control"
							placeholder="Enter Cancelled Receipt Number"
						/>
					</div>
				</div>
			</div>
            <div v-else>
                <label class="">
                    Update Status
                </label>
                <multiselect v-model="status" track-by="key" label="label" :options="filteredOptionStatus" :option-disabled="isOptionDisabled">
					<template #option="{ option }">
						<!-- Custom option layout -->
						<div class="custom-option">
							<span>{{ option.label }}</span>
						</div>
					</template>
				</multiselect>
            </div>
        </template>

        <template #footer>
            <div class="d-flex justify-content-end">
                <button class="btn btn-light" @click="$emit('close')">
                    Cancel
                </button>
                <button class="btn btn-primary" :disabled="isButtonDisabled" :class="{ 'is-loading': isLoading }" @click="update">
                    Update
                </button>
            </div>
        </template>
    </modal>
</template>

<script>
import { mapGetters } from 'vuex';

import Modal from '@/components/Modal.vue';
import DragAndDrop from '@/components/forms/DragAndDrop.vue';
import { duplicateVar } from '@/lib/helper';

export default {
	name: 'UpdateStatusModal',
	props: {
		type: {
			type: String,
			default: '',
		},
		data: {
			type: Object,
			default: () => ({}),
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		Modal,
		DragAndDrop,
	},
	data() {
		return {
			options: [
				{
					key: 'cancelled',
					disabled: false,
					label: 'Cancelled',
				},
				{
					key: 'pending',
					disabled: true,
					label: 'Pending',
				},
				{
					key: 'processing',
					disabled: false,
					label: 'Processing',
				},
				{
					key: 'delivery',
					disabled: false,
					label: 'Delivery',
				},
				{
					key: 'delivered',
					disabled: false,
					label: 'Delivered/Completed',
				},
			],
			receiptNumber: '',
			cancelledReceiptNumber: '',
			status: '',
			posImage: null,
			podImage: null,
			errorMessages: {
				posImage: '',
				podImage: '',
			},
		};
	},
	computed: {
		...mapGetters({
			isDropshipper: 'isDropshipper',
		}),
		label() {
			let label = `Apakah anda yakin ingin menandai barang sudah di terima dengan OrderId : #${this?.data?.order_id}`;

			if (this.data.status === 'processing') {
				label = `Apakah anda yakin ingin menandai barang sudah terkirim dengan OrderId : #${this?.data?.order_id}`;
			}

			return label;
		},
		isButtonDisabled() {
			if (this.type === 'input_receipt_number') {
				return !this.receiptNumber || this.isLoading || !this.posImage;
			}
			if (this.type === 'confirm' && this.data.status !== 'processing') {
				return this.isLoading || !this.podImage;
			}
			return this.isLoading;
		},
		filteredOptionStatus() {
			const data = this.data;
			let options = this.options;
			const paymentStatus = data.payment_status || data?.payment?.status || 'unpaid';
			if (paymentStatus === 'paid') {
				options = this.options.filter(option => option?.key !== 'pending');
			}
			return options;
		},
	},
	mounted() {
		if (this.data) {
			if (this.type === 'input_receipt_number') {
				this.receiptNumber = this.data.shipping?.receipt_number;
				this.cancelledReceiptNumber = this?.data?.shipping?.cancelled_receipt_number;
			}
		}
	},
	methods: {
		update() {
			if (this.type === 'confirm') {
				if (this.data.status === 'processing') {
					this.status = {
						key: 'delivery',
						label: 'Delivery',
						posImage: this.posImage,
					};
				} else {
					this.status = {
						key: 'delivered',
						label: 'Delivered/Completed',
						podImage: this.podImage,
					};
				}
				this.$emit('update', this.status, this.type);
			} else if (this.type === 'input_receipt_number') {
				this.validatePosImage();
				if (this.errorMessages.posImage === '') {
					this.$emit('change-rceipt-number', this.receiptNumber, this.cancelledReceiptNumber, this.posImage);
				}
			} else {
				this.$emit('update', this.status, this.type);
			}
		},
		isOptionDisabled(option) {
			return option.disabled; // Disable condition
		},
		swicth() {
			const currentReceiptNumberCopy = duplicateVar(this.receiptNumber);
			const cancelledReceiptNumberCopy = duplicateVar(this.cancelledReceiptNumber);

			this.receiptNumber = cancelledReceiptNumberCopy;
			this.cancelledReceiptNumber = currentReceiptNumberCopy;
		},
		setPosUrl(value) {
			this.posImage = value;
			this.validatePosImage();
		},
		validatePosImage() {
			this.errorMessages.posImage = '';
			if (this.posImage === null) {
				this.errorMessages.posImage = 'Proof of Shipping (PoS) wajib diisi';
			}
		},
		setPodUrl(value) {
			this.podImage = value;
			this.validatePodImage();
		},
		validatePodImage() {
			this.errorMessages.podImage = '';
			if (this.podImage === null) {
				this.errorMessages.podImage = 'Proof of Delivery (PoD) wajib diisi';
			}
		},
	},
};
</script>
